import * as React from 'react'
import { createPortal } from 'react-dom'
import * as ReactTooltip from 'react-tooltip'
import { mailchimpURL } from '../constants'
import {GASendEvent} from '../ga'
import Icon, { IconName } from './Icon'
import './MailingListModal.scss'
import PrimaryButton from './PrimaryButton'
import SecondaryButton from './SecondaryButton'

enum MailingListStep {
  inactive,
  loading,
  success,
}

interface IState {
  jsLoaded: boolean // whether react's been hydrated by gatsby
  email: string
  step: MailingListStep
  errorCode?: string
  errorMessage?: string
}

type MailingListModalType = 'normal' | 'white' | 'overlay'

interface IProps {
  type?: MailingListModalType
  closeCallback?: () => void
  skipCallback?: () => void
}

class MailingListModal extends React.PureComponent<IProps, IState> {
  public static props: IProps = {
    type: 'normal',
  }

  public state: IState = {
    email: '',
    jsLoaded: false,
    step: MailingListStep.inactive,
  }

  public componentDidMount () {
    this.setState({ jsLoaded: true })
  }

  public clickHandler = (e: React.FormEvent<HTMLElement>) => {
    e.preventDefault()

    fetch('/.netlify/functions/mailchimp-subscribe', {
      body: JSON.stringify({
        email: this.state.email,
      }),
      method: 'POST',
    })
      .then((res: Response) => {
        if (res.ok) {
          return res.text()
        } else {
          return Promise.reject(res)
        }
      })
      .then((data: string) => {
        // Success
        if (data === 'subscribed') {
          GASendEvent('mailinglist', 'signup', 'success');

          this.setState({ step: MailingListStep.success })
          return Promise.resolve(data)
        } else {
          return Promise.reject(data)
        }
      })
      .catch((err: any) => {
        if (err && err.text) {
          err.text().then((data: any) => {
            let message = data
  
            if (message === 'Member Exists') {
              message = 'You\'re already subscribed!'
            }

            GASendEvent('mailinglist', 'signup', 'fail');
  
            this.setState({
              errorCode: err.status,
              errorMessage: message,
              step: MailingListStep.inactive,
            })
          })

          return;
        }

        // If we get an undefined error, show it
        this.setState({
          errorCode: '500',
          errorMessage: 'Sorry, we couldn\'t reach our server! Try again later.',
          step: MailingListStep.inactive,
        })
      })

    this.setState({ step: MailingListStep.loading })
  }

  public emailChangeHanlder = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ email: e.target.value })
  }

  public render() {
    const { type, closeCallback, skipCallback } = this.props
    const { step, email, errorCode, errorMessage, jsLoaded } = this.state

    return (
      <div
        className={`mailingmodal__wrapper mailingmodal__wrapper--${type} theme--yellow`}
      >
        {type === 'overlay' ? (
          <>
            <ReactTooltip
              className="tooltip"
              place="bottom"
              effect="solid"
              aria-haspopup="true"
            />
            <button className="mailingmodal__close" onClick={closeCallback}>
              <Icon name={IconName.x} />
            </button>
          </>
        ) : null}
        <div className={`mailingmodal mailingmodal--${type}`}>
          <div className="mailingmodal__header">
            <img
              className={
                `mailingmodal__icon` +
                (step === MailingListStep.success
                  ? ' mailingmodal__icon--success'
                  : '')
              }
              src={
                step === MailingListStep.success
                  ? require('../img/mail--success.svg')
                  : require('../img/mail.svg')
              }
              alt=""
            />
            <h1>
              {step === MailingListStep.success
                ? 'We did it!'
                : 'We’re building our community.'}
            </h1>
            <p>
              {step === MailingListStep.success
                ? 'Welcome to the Supportive clan; we\'re happy to have you.'
                : 'Join our mailing list to get notified of new conversations!'}
            </p>
          </div>
          {step === MailingListStep.success ? null : (
            <form
              className="mailingmodal__form"
              onSubmit={jsLoaded ? this.clickHandler : () => null}
              action={jsLoaded ? '' : 'https://wearesupportive.us18.list-manage.com/subscribe/post"'}
              method={jsLoaded ? '' : 'POST'}
            >
              <input type="hidden" name="u" value="40067302b02edea4063b16716" />
              <input type="hidden" name="id" value="7e19c9a7b6" />

              {step === MailingListStep.loading ? (
                <div className="mailingmodal__loading__bar">
                  <img
                    className="mailingmodal__loading__bar__spinner"
                    src={require('../img/spinner.svg')}
                    alt=""
                  />
                </div>
              ) : null}
              <input
                className="mailingmodal__email"
                type="email"
                name="MERGE0"
                id="MERGE0"
                value={email}
                onChange={this.emailChangeHanlder}
                placeholder="me@gmail.com"
                disabled={step === MailingListStep.loading}
                required={true}
              />
              {step === MailingListStep.inactive ? (
                <button className="mailingmodal__submit" type="submit">
                  <span>Join</span>
                </button>
              ) : null}
              <input
                type="hidden"
                name="ht"
                value="c487e34fc4d23aeaa7dafed6812fd32e5bebddb4:MTUyNDk0NTk1MS4wMjk="
              />
              <input type="hidden" name="mc_signupsource" value="hosted" />
            </form>
          )}
          {step === MailingListStep.inactive && errorCode && errorMessage ? (
            <div className="mailingmodal__error">
              <span>Error {errorCode}</span> → {errorMessage}
            </div>
          ) : null}
          {type === 'overlay' && step === MailingListStep.success ? (
            <PrimaryButton text="Let's continue" callback={skipCallback} />
          ) : null}
          {type === 'overlay' && step === MailingListStep.inactive ? (
            <SecondaryButton
              className="mailingmodal__skip"
              text="Skip"
              iconName={IconName.skip}
              callback={skipCallback}
            />
          ) : null}
          <footer className="mailingmodal__footer">
            <small>
              P.S. {type === 'overlay' ? 'Sorry to bother you with this!' : ''}{' '}
              We promise to be{' '}
              <span
                className="tooltip__text"
                data-tip="We will not sell or provide your email to anyone for any price. It will be only used for communications from our mailing list."
              >
                respectful
              </span>{' '}
              of your email.
            </small>
          </footer>
        </div>
      </div>
    )
  }
}

export default MailingListModal



// WEBPACK FOOTER //
// ./src/components/MailingListModal.tsx